import Phaser from 'phaser';

const RELOAD_VOLUME = 0.4;
const EMPTY_CLIP_VOLUME = 0.3;
const DEFAULT_RELOAD_TIME_MS = 1000;

export type FireMode = 'single' | 'auto' | 'beam';

export interface WeaponConfig {
  id: string;
  name?: string;
  price: number;
  // Урон одной пулей по врагу. Default 1.
  damage?: number;
  // Гранатомёт: урон в эпицентре и на краю радиуса (linear falloff).
  damageCenter?: number;
  damageEdge?: number;
  // Лазер: урон/сек continuous.
  damagePerSecond?: number;
  // Минимальная задержка между выстрелами (мс). 0 — без cooldown, выстрел на каждый клик/тап.
  fireRateMs: number;
  // Кол-во пуль за выстрел. 1 для пистолета, 5-7 для дробовика.
  bulletsPerShot: number;
  // Полный угол разлёта (градусы). 0 — все пули по базовой линии.
  spreadDeg: number;
  // Ключ звука выстрела (preload в BootScene).
  soundKey: string;
  soundVolume: number;
  // Камера shake на сам выстрел (не путать с shake на попадании).
  shakeShoot: { intensity: number; duration: number } | null;
  // Если undefined — бесконечный магазин (револьвер сейчас).
  magazineSize?: number;
  reloadTimeMs?: number;
  reloadSoundKey?: string;
  emptyClipSoundKey?: string;
  // Размер пули. Если не задано — Bullet берёт DEFAULT (8x4).
  bulletWidth?: number;
  bulletHeight?: number;
  // Скорость пули. Если не задано — DEFAULT (800 px/sec).
  bulletSpeed?: number;
  // 'single' — выстрел только на момент нажатия. 'auto' — пока кнопка зажата.
  fireMode?: FireMode;
  // Парное оружие: пули вылетают по очереди из верхнего и нижнего ствола.
  isDual?: boolean;
  // Время раскрутки в мс. 0/undefined — без раскрутки (моментальный темп).
  windupTimeMs?: number;
  // На старте раскрутки spread × этот множитель, к концу → базовый spread.
  windupSpreadMult?: number;
  // Отдача в пикселях. Каждый выстрел толкает игрока назад на это значение.
  // Игрок стреляет влево, поэтому положительное значение = смещение вправо.
  recoilForce?: number;
  // Гранатомёт: летит вперёд по дуге, AoE-взрыв при приземлении или попадании в врага.
  isGrenade?: boolean;
  explosionRadius?: number;
  flightDurationMs?: number;
  // Дальность полёта в пикселях (от муззла игрока влево).
  rangePx?: number;
  // Высота дуги.
  arcHeight?: number;
  // Лазер: continuous-луч с системой энергии вместо магазина.
  beamWidth?: number;
  maxEnergy?: number;
  energyDrainPerSec?: number;
  energyRegenPerSec?: number;
  minEnergyToFire?: number;
}

const WINDUP_FIRE_RATE_FLOOR = 0.3;

export class Weapon {
  private lastFireTime = 0;
  private ammoInMagazine: number;
  private isReloading = false;
  private lastBarrelIndex = 0;
  private currentWindup = 0;
  private currentEnergy: number;

  constructor(public readonly config: WeaponConfig) {
    this.ammoInMagazine = config.magazineSize ?? Infinity;
    this.currentEnergy = config.maxEnergy ?? 0;
  }

  getFireMode(): FireMode {
    return this.config.fireMode ?? 'single';
  }

  isBeam(): boolean {
    return this.config.fireMode === 'beam';
  }

  // Если уже стреляем — порог 1 энергии (продолжаем пока не выгорело).
  // Если не стреляем — порог minEnergyToFire (нельзя начать с малым запасом).
  canFireBeam(currentlyFiring: boolean): boolean {
    if (!this.isBeam()) return false;
    const threshold = currentlyFiring ? 1 : this.config.minEnergyToFire ?? 10;
    return this.currentEnergy >= threshold;
  }

  drainEnergy(deltaMs: number): void {
    const drainPerSec = this.config.energyDrainPerSec ?? 0;
    this.currentEnergy = Math.max(0, this.currentEnergy - (drainPerSec * deltaMs) / 1000);
  }

  regenEnergy(deltaMs: number): void {
    const regenPerSec = this.config.energyRegenPerSec ?? 0;
    const maxE = this.config.maxEnergy ?? 0;
    this.currentEnergy = Math.min(maxE, this.currentEnergy + (regenPerSec * deltaMs) / 1000);
  }

  getEnergyRatio(): number {
    const maxE = this.config.maxEnergy ?? 0;
    return maxE > 0 ? this.currentEnergy / maxE : 0;
  }

  // Для парных оружий чередует 0/1 (верхний/нижний ствол).
  // Для обычных всегда возвращает 0.
  getNextBarrel(): number {
    if (!this.config.isDual) return 0;
    const current = this.lastBarrelIndex;
    this.lastBarrelIndex = 1 - this.lastBarrelIndex;
    return current;
  }

  canFire(now: number): boolean {
    if (this.isReloading) return false;
    if (now - this.lastFireTime < this.getEffectiveCooldown()) return false;
    if (this.ammoInMagazine <= 0) return false;
    return true;
  }

  // Накапливает раскрутку пока кнопка зажата, сбрасывает при отпускании.
  // Для оружий без windupTimeMs — no-op.
  tickWindup(deltaMs: number, isFiring: boolean): void {
    const windupTime = this.config.windupTimeMs ?? 0;
    if (windupTime === 0) return;
    if (isFiring) {
      this.currentWindup = Math.min(windupTime, this.currentWindup + deltaMs);
    } else {
      this.currentWindup = 0;
    }
  }

  // Сброс раскрутки. Вызывается при переключении оружия (Inventory).
  releaseFire(): void {
    this.currentWindup = 0;
  }

  private getEffectiveCooldown(): number {
    const windupTime = this.config.windupTimeMs ?? 0;
    if (windupTime === 0) return this.config.fireRateMs;
    const ratio = Math.max(WINDUP_FIRE_RATE_FLOOR, this.currentWindup / windupTime);
    return this.config.fireRateMs / ratio;
  }

  private getEffectiveSpread(): number {
    const baseSpread = this.config.spreadDeg;
    const windupTime = this.config.windupTimeMs ?? 0;
    if (windupTime === 0) return baseSpread;
    const mult = this.config.windupSpreadMult ?? 1;
    const ratio = this.currentWindup / windupTime;
    return baseSpread * (mult - (mult - 1) * ratio);
  }

  recordFire(now: number): void {
    this.lastFireTime = now;
    if (this.ammoInMagazine !== Infinity) {
      this.ammoInMagazine--;
    }
  }

  // -1 — маркер бесконечного магазина (для HUD).
  getAmmo(): number {
    return this.ammoInMagazine === Infinity ? -1 : this.ammoInMagazine;
  }

  getMaxAmmo(): number {
    return this.config.magazineSize ?? -1;
  }

  hasInfiniteAmmo(): boolean {
    return this.config.magazineSize === undefined;
  }

  getIsReloading(): boolean {
    return this.isReloading;
  }

  needsReload(): boolean {
    if (this.hasInfiniteAmmo()) return false;
    return this.ammoInMagazine === 0;
  }

  startReload(scene: Phaser.Scene): boolean {
    if (this.isReloading) return false;
    if (this.hasInfiniteAmmo()) return false;
    if (this.ammoInMagazine === this.config.magazineSize) return false;

    this.isReloading = true;

    if (this.config.reloadSoundKey) {
      scene.sound.play(this.config.reloadSoundKey, { volume: RELOAD_VOLUME });
    }

    const reloadTime = this.config.reloadTimeMs ?? DEFAULT_RELOAD_TIME_MS;
    scene.time.delayedCall(reloadTime, () => {
      this.ammoInMagazine = this.config.magazineSize ?? Infinity;
      this.isReloading = false;
    });

    return true;
  }

  playEmptyClickSound(scene: Phaser.Scene): void {
    if (this.config.emptyClipSoundKey) {
      scene.sound.play(this.config.emptyClipSoundKey, { volume: EMPTY_CLIP_VOLUME });
    }
  }

  reset(): void {
    this.ammoInMagazine = this.config.magazineSize ?? Infinity;
    this.lastFireTime = 0;
    this.isReloading = false;
    this.lastBarrelIndex = 0;
    this.currentWindup = 0;
    this.currentEnergy = this.config.maxEnergy ?? 0;
  }

  // Углы для каждой пули относительно baseAngle.
  // Одна пуля + spread > 0 → случайный jitter в пределах ±spread/2 (Узи, АК).
  // Несколько пуль + spread > 0 → равномерный конус (Дробовик).
  // spread = 0 → строго по baseAngle.
  // Для оружий с windup используется effective spread (зависит от раскрутки).
  computeBulletAngles(baseAngle: number): number[] {
    const { bulletsPerShot } = this.config;
    const effectiveSpread = this.getEffectiveSpread();
    if (effectiveSpread === 0) {
      return new Array(bulletsPerShot).fill(baseAngle);
    }
    const spreadRad = (effectiveSpread * Math.PI) / 180;
    if (bulletsPerShot === 1) {
      return [baseAngle + (Math.random() - 0.5) * spreadRad];
    }
    const angles: number[] = [];
    for (let i = 0; i < bulletsPerShot; i++) {
      const t = i / (bulletsPerShot - 1) - 0.5;
      angles.push(baseAngle + t * spreadRad);
    }
    return angles;
  }
}
